'use client';

import { useEffect } from 'react';
import SiteHeader from '@/components/site/SiteHeader';
import { ButtonLink, buttonClass } from '@/components/ui/Button';
import { notifyError, startMonitoring } from '@/lib/monitoring/client';
import { wasReportedOnServer } from '@/lib/monitoring/shared';
import { ROUTES } from '@/lib/site';

type ErrorBoundaryProps = {
  error: Error & { digest?: string };
  reset: () => void;
};

export default function ErrorBoundary({ error, reset }: ErrorBoundaryProps) {
  useEffect(() => {
    startMonitoring();

    // See `global-error.tsx`: a digest means the server already reported it.
    if (wasReportedOnServer(error)) return;

    notifyError(error, { boundary: { scope: 'route', digest: error.digest ?? null } });
  }, [error]);

  return (
    <>
      <SiteHeader />
      <main id="main" className="flex-1 bg-ink-950 text-on-ink">
        <div className="container-page max-w-3xl! py-16 sm:py-24">
          <p className="text-sm font-semibold text-brand-300">Something went wrong</p>
          <h1 className="mt-3 text-[32px] leading-tight font-bold text-white sm:text-5xl">We couldn&apos;t load this page</h1>
          <p className="mt-5 text-lg text-on-ink-muted">Please try again. If it keeps happening, contact support.</p>
          <div className="mt-8 flex flex-col gap-3 sm:flex-row">
            <button type="button" onClick={reset} className={`${buttonClass('primary', 'lg')} focus-ring-on-ink`}>
              Try again
            </button>
            <ButtonLink href={ROUTES.support} variant="on-ink" size="lg">
              Get support
            </ButtonLink>
          </div>
        </div>
      </main>
    </>
  );
}
